import { useLayoutEffect, useState } from "react";
import { Navigate, Routes, Route } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StatsigProvider } from "statsig-react";
import { LabelerGate } from "./components/auth/LabelerGate";
import { Layout } from "./components/Layout";
import { useAuthUser } from "@/utils/firebase";
import { LoginPage } from "./pages/LoginPage";
import { TaskListPage } from "./pages/TaskListPage";
import { TrackLabelingRoute } from "./pages/TrackLabelingPage";

function App() {
  const authUser = useAuthUser();

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 1000 * 60 * 5, // 5 minutes
            retry: 1,
          },
        },
      }),
  );

  // Clear the React Query cache whenever the logged in user changes, e.g. logout, next login
  // , or when another tab switches straight to another user.
  const authUid = authUser?.uid;
  useLayoutEffect(() => {
    queryClient.clear();
  }, [authUid, queryClient]);

  const statsigClientKey = import.meta.env.VITE_STATSIG_CLIENT_KEY;

  const routes = (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/*"
        element={
          <LabelerGate>
            <Layout>
              <Routes>
                <Route path="/" element={<TaskListPage />} />
                <Route path="/tasks/:taskId" element={<TrackLabelingRoute />} />
                {/* Catch all. */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Layout>
          </LabelerGate>
        }
      />
    </Routes>
  );

  if (!statsigClientKey) {
    return (
      <QueryClientProvider client={queryClient}>{routes}</QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <StatsigProvider
        sdkKey={statsigClientKey}
        user={{
          userID: authUser?.uid,
          email: authUser?.email ?? undefined,
        }}
      >
        {routes}
      </StatsigProvider>
    </QueryClientProvider>
  );
}

export default App;
